/**
 * Hilton Analytica COA Platform - Type Definitions
 * 
 * This file contains all TypeScript type definitions for the COA Platform.
 * Types include enums, interfaces, Lambda response types, and API service types.
 */

// =============================================================================
// Enums
// =============================================================================

/**
 * Product categories for COA requests
 */
export type ProductCategory = 
  | 'Diagnostic Test'
  | 'Pharmaceutical'
  | 'Chemical'
  | 'Food Safety'
  | 'Environmental'
  | 'Other';

/**
 * Analysis types available for COA requests
 */
export type AnalysisType = 
  | 'Potency'
  | 'Purity'
  | 'Stability'
  | 'Microbiology'
  | 'Chemistry'
  | 'Other';

/**
 * Status of a COA request
 */
export type RequestStatus = 
  | 'Pending'
  | 'Processing'
  | 'Completed'
  | 'Rejected';

/**
 * Types of queries users can submit
 */
export type QueryType = 
  | 'Status Update'
  | 'Revision Request'
  | 'General Inquiry';

/**
 * Status of a query submission
 */
export type QueryStatus = 
  | 'Pending'
  | 'In Review'
  | 'Resolved';

// =============================================================================
// Interfaces
// =============================================================================

/**
 * COA (Certificate of Analysis) Request
 */
export interface COARequest {
  id: string;
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  productName: string;
  productCategory?: ProductCategory;
  analysisType?: AnalysisType;
  additionalNotes?: string;
  status: RequestStatus;
  submittedAt: string;
  lotNumber?: string;
}

/**
 * Search query parameters
 */
export interface SearchQuery {
  productName?: string;
  lotNumber?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Search result item
 */
export interface SearchResult {
  id: string;
  productName: string;
  lotNumber: string;
  analysisType: string;
  submittedAt: string;
  status: RequestStatus;
  documentKey?: string;
}

/**
 * Query submission from user
 */
export interface QuerySubmission {
  id: string;
  requestId?: string;
  productName?: string;
  email: string;
  queryType: QueryType;
  message: string;
  submittedAt: string;
  status: QueryStatus;
}

// =============================================================================
// Lambda Response Types
// =============================================================================

/**
 * Generic success response from Lambda
 */
export interface LambdaSuccessResponse<T> {
  success: true;
  data: T;
}

/**
 * Error response from Lambda
 */
export interface LambdaErrorResponse {
  success: false;
  error: string;
  code?: string;
}

/**
 * Union type for all Lambda responses
 */
export type LambdaResponse<T> = LambdaSuccessResponse<T> | LambdaErrorResponse;

/**
 * Response from form submission (COA request or query)
 */
export interface SubmissionResponse {
  requestId: string;
  message: string;
  submittedAt: string;
}

/**
 * Response from search endpoint
 */
export interface SearchResponse {
  results: SearchResult[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Response for document URL generation
 */
export interface DocumentUrlResponse {
  url: string;
  expiresAt: string;
}

/**
 * Contact form confirmation response
 */
export interface ContactConfirmationResponse {
  contactId: string;
  message: string;
  submittedAt: string;
}

// =============================================================================
// API Service Types
// =============================================================================

/**
 * API error structure
 */
export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

/**
 * Options for API requests
 */
export interface RequestOptions {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  headers?: Record<string, string>;
}

/**
 * Default request options
 */
export const DEFAULT_REQUEST_OPTIONS: RequestOptions = {
  timeout: 30000,
  retries: 3,
  retryDelay: 1000,
};

/**
 * Form field configuration for dynamic forms
 */
export interface FormFieldConfig {
  name: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'date' | 'textarea' | 'select';
  required?: boolean;
  maxLength?: number;
  pattern?: string;
  patternMessage?: string;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
}

/**
 * Validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Pagination info
 */
export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  total: number;
}